/**
 * DoorLabels — the one `uid -> label` map that lets a door resolve another
 * door's `redeemedBy`/`undoneBy` uid into a human name ("Tür 2 —
 * Seiteneingang") for the operator's screen. Exists only because
 * `users/{uid}` is self-only read (user.entity.ts) — a door genuinely has
 * no other way to resolve a sibling door's uid without widening a rule that
 * protects real member PII.
 *
 * Path: `workspaces/{orgId}/settings/doorLabels` — ONE doc per org.
 * Admin-SDK-write-only: no `create`/`update`/`delete` action is declared, so
 * every client write path is default-denied (the same shape as
 * integration.entity.ts). `provision-member.ts`'s `create-member
 * --roleId=checkin` writes `doors[uid]` alongside the account it creates.
 *
 * Read behind the same `tickets.checkin.scan` token the redeem/unredeem
 * doors already gate on — not `tickets.members.view`, which is a strictly
 * bigger PII surface (names, emails, roles for the whole org) than this
 * doc's single operational label per door account.
 *
 * Fields justified:
 *   doors — the whole doc; a bare uid must never reach the operator's
 *           screen — an operator reads a name, never an id —
 *           and this map is the only lookup that resolves one.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

export const DoorLabelsSchema = z.object({
	doors: z
		.record(z.string(), z.string().min(1))
		.default({})
		.describe(
			"check-in account uid -> its real-world label, e.g. 'Tür 1 — Haupteingang' — provisioned once per account, never client-written",
		),
});
export type DoorLabels = z.infer<typeof DoorLabelsSchema>;

export const DoorLabels = entity<Actor>()({
	schema: DoorLabelsSchema,
	actions: ["read"] as const,
})
	.pipe(({ can, hasPermission, and }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.checkin.scan"),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/settings/doorLabels",
		}),
	);
